// FormRenderer.js
import React, { useState, useEffect, useRef } from "react";
import FormField from "./FormField";
import "./FormRenderer.css";

const cssVariables = `

  :root {
    --bg-header: #F5C8B4;
    --highlight-green: #EAF7E9;
    --highlight-green-text: #1E4620;
    --color-action: #B99B7E;
    --color-dark: #2D2D2D;
    --color-secondary-text: #6c757d;
    --border-color: #e9ecef;
  }
`;
const sectionTitleStyles =
`.item-subtitle {
  font-weight: 600; /* Semi-bold, less heavy than 'bold' */
  font-size: 1rem;
  color: var(--color-dark);
  margin-top: 12px;   /* Adds more space above the subtitle */
  margin-bottom: 12px;
  margin-left: 24px;
  padding-bottom: 6px;
  /* This is the key change: a simple, clean line underneath */
  border-bottom: 1px solid var(--border-color, #e0e0e0);
}`;
const commonPrintStyles = `
  body { font-family: 'Poppins', sans-serif; background-color: #fff; color: var(--color-dark); }
  .print-container { width: 210mm; margin: 1rem auto; padding: 1cm; background: white; box-shadow: 0 0 15px rgba(0,0,0,0.1); }
  .items-table .col-item-id { width: 80px; text-align: left; }
  .print-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid var(--color-dark); padding-bottom: 1rem; margin-bottom: 2rem; }
  .print-header h2 { font-size: 24px; margin: 0; color: var(--color-dark); }
  .print-header .logo { max-height: 80px; max-width: 150px; }
  .section-title { font-size: 16px; font-weight: 600; border-bottom: 1px solid #ccc; padding-bottom: 8px; margin: 2rem 0 1.5rem 0; color: var(--color-dark); }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem 2rem; }
  .info-item label { font-size: 12px; color: var(--color-secondary-text); display: block; margin-bottom: 4px; }
  .info-item .value { font-size: 12px; font-weight: 500; padding: 4px 6px; border: 1px solid var(--border-color); border-radius: 4px; min-height: 28px; word-wrap: break-word; }
  .items-table { width: 100%; border-collapse: collapse; margin-top: 1rem; page-break-inside: auto; }
  .items-table tr { page-break-inside: avoid; page-break-after: auto; }
  .items-table th, .items-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--border-color); font-size: 12px; }
  .items-table th { font-size: 12px; text-transform: uppercase; color: var(--color-secondary-text); }
  .items-table .item-label { font-weight: 600; }
  .items-table .item-desc { font-size: 12px; color: var(--color-secondary-text); }
  .items-table .col-price, .items-table .col-total { text-align: right; width: 120px; }
  .items-table .col-total { font-weight: 700; color: var(--color-action); }
  .items-table .col-qty { text-align: center; width: 60px; }
  .billing-summary { float: right; width: 50%; margin-top: 2rem; page-break-inside: avoid; }
  .billing-summary table { width: 100%; }
  .billing-summary td { padding: 6px 8px; border-bottom: 1px solid var(--border-color); font-size: 12px; }
  .billing-summary .label { font-weight: 500; }
  .billing-summary .value { text-align: right; font-weight: 600; }
  .billing-summary .final-total td { font-weight: 700; background: var(--highlight-green); color: var(--highlight-green-text); border-top: 2px solid #a3d9a5;}
  .signature-section { margin-top: 4rem; padding-top: 2rem; border-top: 1px solid #ccc; display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; page-break-before: always; }
  .signature-box .label { font-size: 14px; margin-top: 8px; }
  .signature-box .signature-line { border-bottom: 1px solid var(--color-dark); height: 60px; }
  .signature-box .signature-name { font-size: 16px; font-weight: 500; padding-top: 30px; }
  .print-controls { text-align: center; position: sticky; bottom: 0; background: white; padding: 1rem; border-top: 1px solid #ccc; }
  .print-button { padding: 12px 24px; font-size: 16px; background: var(--color-action); color: white; border: none; border-radius: 5px; cursor: pointer; }
  .disclaimer-section { page-break-before: auto; margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid #ccc; }
  .disclaimer-text { font-size: 10px; color: #6c757d; line-height: 1.5; text-align: justify; }
  input, select, textarea { font-size: 12px; padding: 4px 6px; height: 28px; line-height: 1.2; border: 1px solid #999; border-radius: 4px; }
  textarea { min-height: 40px; resize: none; }
  @media print {
    body { background-color: white; }
    .print-container { margin: 0; padding: 2cm 0cm; box-shadow: none; width: 100%; }
    .print-controls, .scroll-fab-container { display: none !important; }
  }
`;

const blankFormStyles = `
  .blank-input-box {
    border: 1px solid #999;
    border-radius: 3px;
    padding: 2px 4px;
    min-height: 22px;
    background-color: #f8f9fa;
    font-size: 11px;
    line-height: 1.2;
  }
  .blank-checkbox {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 1px solid var(--color-dark);
    vertical-align: middle;
    border-radius: 2px;
  }
  .items-table .col-select { 
    width: 40px; 
    text-align: center; 
  }
  .items-table th, .items-table td {
    font-size: 11px;
    padding: 4px 6px;
  }
  .info-item .value {
    font-size: 11px;
    padding: 2px 4px;
    border: 1px solid var(--border-color);
    border-radius: 3px;
    min-height: 22px;
  }
  textarea {
    font-size: 11px;
    padding: 2px 4px;
    min-height: 28px;
    border: 1px solid #999;
    border-radius: 3px;
    resize: none;
  }
`;


const FormRenderer = ({
  formTemplate,
  formData,
  onFormChange,
  onSubmit,
  onSaveForLater, // New prop
  onReturnToBroker,
  onFinalize,
  errors,
  calculatedTotals,
  isSaving,
  formStatus,
  displayInvoiceId,
  userRole,
  onBackToDashboard,
  baseInvoiceId,
  isReadOnlyOverride = false,
  isActionable = true,
}) => {
  const [openSections, setOpenSections] = useState(
    formTemplate?.sections?.reduce(
      (acc, section) => ({ ...acc, [section.order]: true }),
      {}
    )
  );
  const [showScroll, setShowScroll] = useState({ up: false, down: true });
  const formContainerRef = useRef(null);

  useEffect(() => {
    const container = formContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollThreshold = 200;
      const showUp = scrollTop > scrollThreshold;
      const showDown =
        scrollTop + clientHeight < scrollHeight - scrollThreshold;
      setShowScroll({ up: showUp, down: showDown });
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollUp = () => {
    formContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleScrollDown = () => {
    formContainerRef.current?.scrollTo({
      top: formContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const handlePrintBlankForm = () => {
    const templateData = JSON.parse(JSON.stringify(formData));
    const fieldsToPreserve = [
      "lotNumber",
      "housePlanName",
      "elevation",
      "housePlanImageUrl",
      "housePlanId",
      "display_date",
    ];

    if (templateData.header) {
      formTemplate.headerInputFields.forEach((field) => {
        if (
          !fieldsToPreserve.includes(field.fieldName) &&
          !field.isAutoFilled
        ) {
          templateData.header[field.fieldName] = "";
        }
      });
    }

    if (templateData.selections) {
      Object.keys(templateData.selections).forEach((key) => {
        const selection = templateData.selections[key];
        selection.selected = false;
        selection.comment = "";
        selection.quantity = 1;
        selection.manualPrice = 0;
      });
    }

    templateData.billing = {};
    templateData.signatures = {};
    openPrintPreviewWithData(templateData, true);
  };

  const openPrintPreviewWithData = (currentData, isBlank = false) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to print the form.");
      return;
    }

    let generatedHtml = "";
    const invoiceToShow = isBlank ? baseInvoiceId : displayInvoiceId;

    generatedHtml += `<div class="print-header">
            <div>
                <h2>${formTemplate.templateName}</h2>
                <p>Invoice #: ${invoiceToShow || ""}</p>
            </div>
            ${
              currentData.header?.housePlanImageUrl
                ? `<img src="${currentData.header.housePlanImageUrl}" class="logo" />`
                : ""
            }
        </div>`;

    generatedHtml += `<div class="section-title">Homeowner & Property Information</div><div class="info-grid">`;
    formTemplate.headerInputFields.forEach((field) => {
      const value = currentData.header?.[field.fieldName];
      let displayValue;

      if (
        (field.fieldType === "date" || field.fieldType === "display_date") &&
        value
      ) {
        const date = new Date(value);
        date.setDate(date.getDate() + 1);
        displayValue = date.toLocaleDateString("en-US");
      } else {
        displayValue = value || "N/A";
      }

      if (isBlank && !value) {
        displayValue = `<div class="blank-input-box"> </div>`;
      }

      generatedHtml += `<div class="info-item"><label>${field.label}</label><div class="value">${displayValue}</div></div>`;
    });
    generatedHtml += `</div>`;

    formTemplate.sections.forEach((section) => {
      generatedHtml += `<div class="section-title">${
        section.title
      }</div><table class="items-table"><thead><tr >
                ${isBlank ? '<th class="col-select">Select</th>' : ""}
                  <th>Item ID</th><th>Description</th><th class="col-price">Unit Price</th><th class="col-qty">Qty</th><th class="col-total">Total</th>
            </tr></thead><tbody>`;
      section.items.forEach((item) => {
        const selection = currentData.selections?.[item.fieldName];
      const displayPrice =
        !isBlank && item.fieldType === "manual_entry" && selection?.manualPrice !== undefined
          ? parseFloat(selection.manualPrice) || 0 // Use || 0 to ensure it's a number
          : parseFloat(item.price) || 0; // Use || 0 to ensure it's a number

      const quantityForCalculation = !isBlank && item.fieldType !== "display_static"
        ? (Number(selection?.quantity) || 1) // Use Number() to ensure it's a number, default to 1
        : 1;

      const totalForCalculation = displayPrice * quantityForCalculation;
      // --- END OF NEW CODE BLOCK ---
        if (isBlank || (selection && selection.selected)) {
                         generatedHtml += `<tr>
                    ${
                      isBlank
                        ? '<td><div class="blank-checkbox"></div></td>'
                        : ""
                    }
                    <td class="col-item-id">${item.itemId}</td> 
                    <td>
                        <div class="item-label">${item.label}</div>
                        <div class="item-desc">${
                          item.description || ""
                        }</div>
                    </td>
                    <td class="col-price">${(
                      // --- THIS LINE IS CHANGED ---
                      isBlank && item.fieldType !== "manual_entry" ? (item.price || 0) : 
                      isBlank && item.fieldType === "manual_entry" ? "_____" : displayPrice
                    ).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}</td>
                    <td class="col-qty">${
                      // --- THIS LINE IS CHANGED ---
                      isBlank ? "___" : quantityForCalculation
                    }</td>
                    <td class="col-total">${
                      // --- THIS LINE IS CHANGED ---
                      isBlank
                        ? "_____"
                        : totalForCalculation.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                    }</td>
                </tr>`;
        }
      });
      generatedHtml += `</tbody></table>`;
    });

    generatedHtml += `<div class="billing-summary" style="width: 100%; "><table><tbody>`;
    formTemplate.billingSection.items.forEach((item) => {
      let valueStr;
        let breakdownStr = ""; 
      if (isBlank) {
        valueStr = `<div class="blank-input-box" style="text-align:right;"> </div>`;
      } else if (item.fieldType === "manual_input_currency") {
        valueStr = (
          parseFloat(currentData.billing?.[item.fieldName]) || 0
        ).toLocaleString("en-US", { style: "currency", currency: "USD" });
      } else {
        valueStr = (calculatedTotals?.[item.fieldName] || 0).toLocaleString(
          "en-US",
          { style: "currency", currency: "USD" }
        );
      }
       if (item.fieldName === "deposit") {
            if (isBlank) {
              breakdownStr = `<br><small style="color:#6c757d; font-style:italic;">
                (5% Base: _____ + 15% Upgrades: _____)
              </small>`;
            } else {
              const baseDeposit = (calculatedTotals?.baseHomeTotal || 0) * 0.05;
              const upgradeDeposit =
                (calculatedTotals?.structuralUpgradesTotal || 0) * 0.15;

              breakdownStr = `<br><small style="color:#6c757d; font-style:italic;">
                (5% Base: ${baseDeposit.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })} + 15% Upgrades: ${upgradeDeposit.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })})
              </small>`;
            }
          }


      generatedHtml += `<tr class="${
        item.fieldName === "finalPrice" ? "final-total" : ""
      }">
                 <td class="label">${item.label}</td>
                 <td class="value" style="width: 50%;">${valueStr}${breakdownStr}</td>
            </tr>`;
    });
    generatedHtml += `</tbody></table></div><div style="clear:both;"></div>`;

    generatedHtml += `<div class="disclaimer-section">
        <p class="disclaimer-text">
        Plans and elevations/renderings are artist’s concept and may contain options, upgrades,
        premiums or features which are not standard on all models. Any option, upgrade, premium, finishing item or model, such as tile, marble, carpet, cabinets, stone, brickwork, wood, paint, stain and mica, is subject to size and color variations, grain and quality variations, and may vary in accordance with price, availability and changes by manufacturers from those shown in the model, if any, or in illustrations or brochures or those included in the specifications. Estates at White Oak reserves the right to change subcontractors, suppliers, manufacturers, brand names or items and to substitute equipment, materials, appliances, brands, etc., which in Estates at White Oak’s opinion are of quality substantially similar or equal, or of better quality, subject to their availability. All lots and homes are subject to prior sale, and this Home Options Cost Estimate in no way constitutes any reservation, purchase agreement or commitment to sell.
        </p>
        <p class="disclaimer-text">
        Purchasers will cooperate and adjust clerical errors, including the execution or re-execution of
        any reasonable documentation and/or remittance of any additional sums in the Purchase Agreement. If applicable, the Purchase and Sale Agreement will identify the features designated as an Everything’s Included® feature and will include additional information, disclosures, and disclaimers relating to the home and its features. Prices, features, options, design and materials are subject to change without notice. Plans, features, options, exterior designs, materials and dimensions are subject to change without notice. Square footage and dimensions are estimated and may vary in actual finished construction. Floor plans and exterior designs are artist’s conception and are not intended to show specific detail. All information deemed to be correct but not guaranteed. Plans are the property of the builder, all rights reserved. Included features are subject to change.
        </p>
      </div>`;

    if (
      formTemplate.DigitalSignatureSection &&
      Array.isArray(formTemplate.DigitalSignatureSection)
    ) {
      generatedHtml += `<div class="signature-section">`;
      formTemplate.DigitalSignatureSection.forEach((sigSection) => {
        sigSection.items.forEach((field) => {
          const sigValue = isBlank
            ? '<div class="signature-content"></div>'
            : currentData.signatures?.[field.fieldName]
            ? `<span style="font-size:18px;font-weight:bold;">${
                currentData.signatures[field.fieldName]
              }</span>`
            : "<span>(Not Signed)</span>";
          generatedHtml += `<div class="signature-box">
                              <div class="signature-line">${sigValue}</div>
                              <div class="label">${field.label}</div>
                          </div>`;
        });
      });
      generatedHtml += `</div>`;
    }

    printWindow.document.write(
      `<html><head><title>Print Preview</title><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"><style>${cssVariables}${commonPrintStyles}${
        isBlank ? blankFormStyles : ""
      }</style></head><body><div class="print-container">${generatedHtml}</div><div class="print-controls"><button class="print-button" onclick="window.print()">Print Document</button></div></body></html>`
    );
    printWindow.document.close();
  };

  const handlePreviewAndPrint = () => openPrintPreviewWithData(formData, false);

  if (!formTemplate || !formData) {
    return (
      <div className="p-5 text-center">
        <h3>Initializing form...</h3>
      </div>
    );
  }

  const isReadOnly =
    userRole === "manager" ||
    (userRole === "broker" && formStatus !== "pending") ||
    formStatus === "finalized" ||
    formStatus === "reassigned" ||
    isReadOnlyOverride;

  const alwaysReadOnlyHeaderFields = [
    "lotNumber",
    "invoice",
    "housePlan",
    "elevation",
    "housePlanId",
    "housePlanName",
  ];
  const {
    templateName,
    headerInputFields,
    sections,
    billingSection,
    commentSection,
    DigitalSignatureSection,
  } = formTemplate;
  const { housePlanImageUrl } = formData.header || {};

  const toggleSection = (order) => {
    setOpenSections((prev) => ({ ...prev, [order]: !prev[order] }));
  };

  const renderActionButtons = () => {
    const printButtons = (
      <>
        <button
          type="button"
          className="btn btn-info"
          onClick={handlePreviewAndPrint}
          disabled={isSaving}
        >
          Preview & Print
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handlePrintBlankForm}
          disabled={isSaving}
        >
          Print Blank Form
        </button>
      </>
    );

    if (formStatus === "pending" && userRole === "broker") {
      return (
        <>
          {printButtons}
          {/* NEW SAVE BUTTON */}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onSaveForLater}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save for Later"}
          </button>
          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={isSaving}
          >
            {isSaving ? "Submitting..." : "Submit Form"}
          </button>
        </>
      );
    }

    if (formStatus === "submitted" && userRole === "manager" && isActionable) {
      return (
        <>
          {printButtons}
          <button
            type="button"
            className="btn btn-warning"
            onClick={onReturnToBroker}
            disabled={isSaving}
          >
            Return to Broker
          </button>
          <button
            type="button"
            className="btn btn-success px-4"
            onClick={onFinalize}
            disabled={isSaving}
          >
            Finalize & Approve
          </button>
        </>
      );
    }

    return (
      <>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBackToDashboard}
        >
          ← Back to Dashboard
        </button>
        {printButtons}
      </>
    );
  };

  return (
    <div className="form-renderer-container">
      <style>{cssVariables + sectionTitleStyles + commonPrintStyles}</style>
      <div className="scroll-fab-container">
        {showScroll.up && (
          <button
            onClick={handleScrollUp}
            className="scroll-fab"
            title="Scroll to Top"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
              />
            </svg>
          </button>
        )}
        {showScroll.down && (
          <button
            onClick={handleScrollDown}
            className="scroll-fab"
            title="Scroll to Bottom"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
              />
            </svg>
          </button>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (formStatus === "pending") onSubmit();
        }}
      >
        <div className="form-header">
          <div>
            <h2 className="mb-0">{templateName}</h2>
            {formStatus === "submitted" ||
            formStatus === "finalized" ||
            formStatus === "reassigned" ? (
              <span className="badge badge-invoice">
                Invoice #: {displayInvoiceId}
              </span>
            ) : (
              <span className="badge bg-warning text-dark">
                Status:{" "}
                {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
              </span>
            )}
          </div>
          {housePlanImageUrl && (
            <img
              src={housePlanImageUrl}
              alt="House Plan"
              className="form-logo"
            />
          )}
        </div>

        <div className="form-content-wrapper" ref={formContainerRef}>
          <h5 className="section-title">Homeowner & Property Information</h5>
          <div className="form-section-card">
            <div className="row g-4">
              {headerInputFields
                ?.sort((a, b) => a.order - b.order)
                .map((field) => (
                  <div key={field.fieldName} className="col-md-6 col-lg-4">
                    <FormField
                      field={field}
                      value={formData.header?.[field.fieldName]}
                      onFormChange={(fieldName, value) =>
                        onFormChange("header", fieldName, value)
                      }
                      error={errors?.[field.fieldName]}
                      isReadOnly={
                        isReadOnly ||
                        field.isAutoFilled ||
                        alwaysReadOnlyHeaderFields.includes(field.fieldName)
                      }
                    />
                  </div>
                ))}
            </div>
          </div>

          {sections
            ?.sort((a, b) => a.order - b.order)
            .map((section) => (
              <div key={section.order} className="collapsible-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection(section.order)}
                >
                  <h5>{section.title}</h5>
                  <i
                    className={`bi bi-chevron-down section-toggle-icon ${
                      !openSections?.[section.order] ? "collapsed" : ""
                    }`}
                  ></i>
                </div>
                {openSections?.[section.order] && (
                  <div className="section-content">
                    {section.items
                      .sort((a, b) => a.order - b.order)
                      .map((item) => (
                        <FormField
                          key={item.itemId}
                          field={item}
                          value={formData.selections?.[item.fieldName]}
                          onFormChange={(fieldName, value) =>
                            onFormChange("selections", fieldName, value)
                          }
                          error={errors?.[item.fieldName]}
                          isReadOnly={isReadOnly}
                        />
                      ))}
                  </div>
                )}
              </div>
            ))}

          <h5 className="section-title">{billingSection?.title}</h5>
          <ul className="billing-summary list-unstyled p-0">
            {billingSection?.items
              ?.sort((a, b) => a.order - b.order)
              .map((item) => (
                <React.Fragment key={item.fieldName}>
                  <li
                    className={
                      item.fieldName === "finalPrice" ||
                      item.fieldName === "balanceAtClosing"
                        ? "final-total"
                        : ""
                    }
                  >
                    <h6 className="billing-label mb-0">
                      {item.label}
                      {item.depositPercentage && (
                        <span className="badge bg-secondary ms-2">
                          {item.depositPercentage * 100}% Deposit
                        </span>
                      )}
                    </h6>
                    <div className="billing-value">
                      {item.fieldType === "manual_input_currency" ? (
                        <div
                          className="input-group-form"
                          style={{ width: "150px" }}
                        >
                          <input
                            type="number"
                            className="form-control text-end"
                            value={formData.billing?.[item.fieldName] || ""}
                            onChange={(e) =>
                              onFormChange(
                                "billing",
                                item.fieldName,
                                e.target.value
                              )
                            }
                            min="0"
                            disabled={isSaving || isReadOnly}
                          />
                        </div>
                      ) : (
                        <span>
                          {item.fieldName === "deposit" && (
                            <small
                              className="text-muted d-block"
                              style={{ marginTop: "-5px", fontSize: "0.8em" }}
                            >
                              (5% Base:{" "}
                              {(
                                (calculatedTotals?.baseHomeTotal || 0) * 0.05
                              ).toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                              {" + "} 15% Upgrades:{" "}
                              {(
                                (calculatedTotals?.structuralUpgradesTotal ||
                                  0) * 0.15
                              ).toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                              )
                            </small>
                          )}
                          <span
                            className={item.isNegative ? "text-danger" : ""}
                          >
                            {item.isNegative && "-"}
                            {(
                              calculatedTotals?.[item.fieldName] || 0
                            ).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </span>
                        </span>
                      )}
                    </div>
                  </li>
                  {(item.fieldName === "designUpgradesTotal" ||
                    item.fieldName === "concessions") && (
                      
                    <li className="billing-comment-row">
                      <textarea
                        className="form-control form-control-sm"
                        placeholder={`Comments for ${item.label}...`}
                        value={
                          formData.billing?.[`${item.fieldName}Comment`] || ""
                        }
                        onChange={(e) =>
                          onFormChange(
                            "billing",
                            `${item.fieldName}Comment`,
                            e.target.value
                          )
                        }
                        disabled={isSaving || isReadOnly}
                        rows="2"
                      />
                    </li>
                  )}
                </React.Fragment>
              ))}
          </ul>

          {commentSection?.items?.map((item) => (
            <div key={item.fieldName} className="mb-4">
              <h5 className="section-title" style={{ paddingTop: 0 }}>
                {item.label}
              </h5>
              <div className="notes-section">
                <FormField
                  field={item}
                  value={formData.billing?.[item.fieldName]}
                  onFormChange={(fieldName, value) =>
                    onFormChange("billing", fieldName, value)
                  }
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          ))}

          <div className="disclaimer-section">
            <h5 className="section-title">Disclaimer</h5>
            <div className="disclaimer-text">
              <p>
                Plans and elevations/renderings are artist’s concept and may
                contain options, upgrades, premiums or features which are not
                standard on all models. Any option, upgrade, premium,
                finishing item or model, such as tile, marble, carpet,
                cabinets, stone, brickwork, wood, paint, stain and mica, is
                subject to size and color variations, grain and quality
                variations, and may vary in accordance with price,
                availability and changes by manufacturers from those shown in
                the model, if any, or in illustrations or brochures or those
                included in the specifications. Estates at White Oak reserves
                the right to change subcontractors, suppliers, manufacturers,
                brand names or items and to substitute equipment, materials,
                appliances, brands, etc., which in Estates at White Oak’s
                opinion are of quality substantially similar or equal, or of
                better quality, subject to their availability. All lots and
                homes are subject to prior sale, and this Home Options Cost
                Estimate in no way constitutes any reservation, purchase
                agreement or commitment to sell.
              </p>
              <p>
                Purchasers will cooperate and adjust clerical errors, including
                the execution or re-execution of any reasonable documentation
                and/or remittance of any additional sums in the Purchase
                Agreement. If applicable, the Purchase and Sale Agreement will
                identify the features designated as an Everything’s Included®
                feature and will include additional information, disclosures,
                and disclaimers relating to the home and its features. Prices,
                features, options, design and materials are subject to change
                without notice. Plans, features, options, exterior designs,
                materials and dimensions are subject to change without notice.
                Square footage and dimensions are estimated and may vary in
                actual finished construction. Floor plans and exterior designs
                are artist’s conception and are not intended to show specific
                detail. All information deemed to be correct but not
                guaranteed. Plans are the property of the builder, all rights
                reserved. Included features are subject to change.
              </p>
            </div>
          </div>

          {DigitalSignatureSection &&
            Array.isArray(DigitalSignatureSection) &&
            DigitalSignatureSection.map((sigSection, index) => (
              <React.Fragment key={index}>
                <h5 className="section-title">{sigSection.title}</h5>
                <div className="form-section-card">
                  <div className="row g-5 p-4">
                    {sigSection.items
                      .sort((a, b) => a.order - b.order)
                      .map((item) => (
                        <div key={item.fieldName} className="col-md-6">
                          <FormField
                            field={item}
                            value={formData.signatures?.[item.fieldName]}
                            onFormChange={(fieldName, value) =>
                              onFormChange("signatures", fieldName, value)
                            }
                            isReadOnly={isReadOnly}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
        </div>

        <div className="form-footer">{renderActionButtons()}</div>
      </form>
    </div>
  );
};

export default FormRenderer;